import { useQuery } from '@tanstack/react-query';

import { useEnvironmentId } from '@/react/hooks/useEnvironmentId';
import { EnvironmentId } from '@/react/portainer/environments/types';
import axios, { parseAxiosError } from '@/portainer/services/axios/axios';
import { withError } from '@/react-tools/react-query';

type DescribeAPIParams = {
  name: string;
  kind: string;
  namespace?: string;
};

type DescribeResourceResponse = {
  describe: string;
};

async function getDescribeResource(
  environmentId: number,
  name: string,
  resourceType?: string,
  namespace?: string
) {
  try {
    // This should never happen, but to keep the linter happy...
    if (!name || !resourceType) {
      throw new Error('Name and kind are required');
    }

    const params: DescribeAPIParams = {
      name,
      namespace,
      kind: resourceType,
    };

    const { data } = await axios.get<DescribeResourceResponse>(
      `kubernetes/${environmentId}/describe`,
      {
        params,
      }
    );
    return data;
  } catch (err) {
    throw parseAxiosError(err, 'Unable to retrieve resource details');
  }
}

export function useDescribeResource(
  name: string,
  resourceType?: string,
  namespace?: string,
  enabled: boolean = true,
  // environmentId overrides the route-derived id for callers rendered outside a
  // kubernetes route (e.g. the add-on details view).
  environmentId?: EnvironmentId
) {
  const routeEnvironmentId = useEnvironmentId(false);
  const envId = environmentId ?? routeEnvironmentId;

  return useQuery(
    [envId, 'kubernetes', 'describe', namespace, resourceType, name],
    () => getDescribeResource(envId, name, resourceType, namespace),
    {
      enabled: enabled && !!envId && !!name && !!resourceType,
      ...withError('Enable to retrieve data for resource'),
    }
  );
}
